import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Alert,
  Modal
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { AuthContext } from '../contexts/AuthContext';
import { SheetContext } from '../contexts/SheetContext';
import sheetService from '../services/sheetService';
import alertService from '../services/alertService';
import hapticService from '../services/hapticService';
import LoadingScreen from '../components/LoadingScreen';
import { useRetryCountdown } from '../hooks/useRetryCountdown';
import { config } from '../config';
import AsyncStorageHelper from '../services/asyncStorageHelper';
import AdminQuickEditModal from '../components/AdminQuickEditModal';

const ScoringScreen = ({ route, navigation }) => {
  const { 
    competitionName, 
    riderName, 
    riderNumber, 
    riderCountry, 
    laps, 
    sections,
    assignedSection // Nouvelle prop pour la section assignée
  } = route.params;
  
  const { user, getValidAccessToken, signOut, isUserAdmin } = useContext(AuthContext);
  const { sheetId, refreshInterval } = useContext(SheetContext);
  
  const [selectedLap, setSelectedLap] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [availableSections, setAvailableSections] = useState([]);
  const [scores, setScores] = useState({});
  const [initialScores, setInitialScores] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [hasUserChangedSection, setHasUserChangedSection] = useState(false);

  // 🆕 Charger les secteurs disponibles au montage
  useEffect(() => {
    const loadAvailableSections = async () => {
      if (!sheetId || !competitionName || !getValidAccessToken) return;
      
      try {
        const data = await sheetService.getRiderData(sheetId, competitionName, getValidAccessToken);
        setAvailableSections(data.availableSections || Array.from({length: sections}, (_, i) => i + 1));
      } catch (error) {
        console.error('Error loading available sections:', error);
        // Fallback avec les secteurs 1..sections
        setAvailableSections(Array.from({length: sections}, (_, i) => i + 1));
      }
    };
    
    loadAvailableSections();
  }, [sheetId, competitionName, sections, getValidAccessToken]);

  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [showAdminQuickEdit, setShowAdminQuickEdit] = useState(false);
  
  // États pour les nouvelles fonctionnalités
  const [referenceSectionForSession, setReferenceSectionForSession] = useState(null);
  const [hasShownSectionChangeWarning, setHasShownSectionChangeWarning] = useState(false);
  const [isConsultingScores, setIsConsultingScores] = useState(false);
  const [showSectionModal, setShowSectionModal] = useState(false); // Changé de showSectionSelection à showSectionModal
  const [consultingSelectedLap, setConsultingSelectedLap] = useState(1);
  
  const pointValues = [0, 10, 20, 30, 40, 50, 60, '-'];

  // Hook pour le countdown du retry
  const { getButtonText, isDisabled, startCountdown, resetCountdown } = useRetryCountdown(config.RETRY_COUNTDOWN_SECONDS);

  // Démarrer le countdown seulement pour les erreurs non-session
  useEffect(() => {
    if (error && errorType && errorType !== 'session') {
      startCountdown();
    } else {
      resetCountdown();
    }
  }, [error, errorType, startCountdown, resetCountdown]);

  // Initialiser avec la section assignée passée en paramètre - APRÈS le chargement des scores
  useEffect(() => {
    // 🔧 AJOUTER LA CONDITION hasUserChangedSection
    if (assignedSection && 
        assignedSection >= 1 && 
        assignedSection <= sections && 
        Object.keys(scores).length > 0 && 
        !hasUserChangedSection) { // Ne pas réinitialiser si changement manuel
      setSelectedSection(assignedSection);
      setReferenceSectionForSession(assignedSection);
      console.log('Using assigned section from params:', assignedSection);
    }
  }, [assignedSection, sections, scores, hasUserChangedSection]);

  // Fonction pour vérifier si un score existait au chargement initial
  const wasScoreInitiallyPresent = (lapSection) => {
    const initialScore = initialScores[lapSection];
    return initialScore !== undefined && initialScore !== '-' && initialScore !== '';
  };
  
  const areAllLapsComplete = () => {
    for (let lapNum = 1; lapNum <= laps; lapNum++) {
      if (!isLapComplete(lapNum)) {
        return false;
      }
    }
    return true;
  };
    
  const isLapComplete = (lapNumber) => {
    // Sécurité : si availableSections n'est pas encore chargé, retourner false
    if (!availableSections || availableSections.length === 0) {
      return false;
    }
    
    // Utiliser availableSections au lieu de 1..sections
    for (const sectionNum of availableSections) {
      const lapSection = `LAP${lapNumber}_S${sectionNum}`;
      const score = scores[lapSection];
      
      if (!score || score === '-') {
        return false;
      }
    }
    return true;
  };

  // Fonction pour ouvrir le modal admin
  const openAdminQuickEdit = () => {
  hapticService.button();
  setShowAdminQuickEdit(true);
  };

  const closeAdminQuickEdit = () => {
    setShowAdminQuickEdit(false);
    // Recharger les scores après modification
    fetchRiderScores();
  };

  // Trouver le prochain tour disponible
  const getNextAvailableLap = () => {
    for (let lapNum = 1; lapNum <= laps; lapNum++) {
      if (!isLapComplete(lapNum)) {
        return lapNum;
      }
    }
    return laps;
  };

  // Sélection automatique du tour suivant
  const checkAndAutoSelectNextLap = () => {
    if (!selectedLap || !config.ENABLE_AUTO_LAP_SELECTION) return;
    
    if (isLapComplete(selectedLap)) {
      const nextLap = selectedLap < laps ? selectedLap + 1 : selectedLap;
      
      if (nextLap !== selectedLap) {
        console.log(`🎯 Auto-selecting next lap: ${selectedLap} -> ${nextLap}`);
        setSelectedLap(nextLap);
        
        if (config.AUTO_LAP_FEEDBACK) {
          hapticService.selection();
        }
        
        if (config.ENABLE_AUTO_LAP_NOTIFICATIONS) {
          alertService.showAlert(
            'Lap Complete',
            `Lap ${selectedLap} completed! Moved to Lap ${nextLap}.`,
            [{ text: 'OK' }]
          );
        }
      } else if (selectedLap === laps && areAllLapsComplete()) {
        console.log('🏁 All laps completed!');
        if (config.AUTO_LAP_FEEDBACK) {
          hapticService.success();
        }
        
        if (config.ENABLE_AUTO_LAP_NOTIFICATIONS) {
          alertService.showAlert(
            'All Laps Complete!',
            `🏁 All ${laps} laps have been completed for ${riderName}.`,
            [{ text: 'Great!' }]
          );
        }
      }
    }
  };

  // Initialisation intelligente du tour
  const initializeSmartLapSelection = () => {
    if (selectedLap === null && Object.keys(scores).length > 0 && config.ENABLE_AUTO_LAP_SELECTION) {
      const smartLap = getNextAvailableLap();
      console.log('🎯 Smart lap initialization:', smartLap);
      setSelectedLap(smartLap);
    }
  };

  useEffect(() => {
    if (selectedLap === null && Object.keys(scores).length > 0) {
      initializeSmartLapSelection();
    }
  }, [scores, selectedLap]);

  // Gestion du changement de secteur avec protection
  const handleSectionChange = async (section, isConsulting = false) => {
    hapticService.selection();
    
    if (isConsulting) {
      setSelectedSection(section);
      return;
    }
    
    if (referenceSectionForSession === null) {
      setSelectedSection(section);
      setReferenceSectionForSession(section);
      setShowSectionModal(false);
      await saveSectionToStorage(section);
      return;
    }
    
    if (section !== referenceSectionForSession) {
      alertService.showAlert(
        'Change Assigned Section',
        `You are changing your assigned section from S${referenceSectionForSession} to S${section}.\n\nMake sure this is correct to avoid scoring errors.`,
        [
          { 
            text: 'Cancel', 
            style: 'cancel',
            onPress: () => {
              hapticService.button();
              setShowSectionModal(false);
            }
          },
          { 
            text: 'Confirm Change', 
            onPress: async () => {
              hapticService.button();
              setSelectedSection(section);
              setReferenceSectionForSession(section);
              setShowSectionModal(false);
              setHasShownSectionChangeWarning(false);
              setHasUserChangedSection(true); // 🔧 MARQUER LE CHANGEMENT MANUEL
              await saveSectionToStorage(section);
              console.log(`🔄 Assigned section changed to S${section}`);
            }
          }
        ]
      );
      return;
    }
    
    setSelectedSection(section);
    setShowSectionModal(false);
  };

  // Sauvegarder le secteur en storage
  const saveSectionToStorage = async (section) => {
    if (!config.ENABLE_SECTION_MEMORY) return;
    
    try {
      const sectionNum = parseInt(section, 10);
      if (isNaN(sectionNum) || sectionNum < 1) {
        console.error('Invalid section number:', section);
        return;
      }
      
      const success = await AsyncStorageHelper.setItem('lastSelectedSection', sectionNum);
      if (success) {
        console.log('Saved selected section:', sectionNum);
      } else {
        console.error('Failed to save selected section');
      }
    } catch (error) {
      console.error('Error saving selected section:', error);
    }
  };

  // Handle lap selection
  const handleLapChange = (lap) => {
    hapticService.selection();
    setSelectedLap(lap);
  };

  // Handle consultation lap change
  const handleConsultingLapChange = (lap) => {
    hapticService.selection();
    setConsultingSelectedLap(lap);
  };

  const fetchRiderScores = useCallback(async () => {
    if (!sheetId || !user || !competitionName || !riderName || !getValidAccessToken) return;
    
    setIsLoading(true);
    setError(null);
    setErrorType(null);
    
    try {
      const riderScores = await sheetService.getRiderScore(
        sheetId,
        competitionName,
        riderName,
        getValidAccessToken
      );
      
      setScores(riderScores);
      
      if (Object.keys(initialScores).length === 0) {
        setInitialScores(riderScores);
        console.log('Initial scores saved:', riderScores);
      }
      
    } catch (e) {
      console.error('Error fetching rider scores:', e);
      
      if (e instanceof sheetService.ApiError) {
        setErrorType(e.type);
      } else {
        setErrorType('network');
      }
      
      setError('Failed to fetch rider scores');
      hapticService.error();
    } finally {
      setIsLoading(false);
    }
  }, [sheetId, competitionName, riderName, user, getValidAccessToken, initialScores]);
  
  useEffect(() => {
    fetchRiderScores();
  }, [fetchRiderScores]);
  
  useEffect(() => {
    if (refreshInterval <= 0) return;
    
    const intervalId = setInterval(() => {
      fetchRiderScores();
    }, refreshInterval * 1000);
    
    return () => clearInterval(intervalId);
  }, [fetchRiderScores, refreshInterval]);

  const handleRetry = async () => {
    if (isDisabled || errorType === 'session') return;
    
    hapticService.button();
    try {
      await fetchRiderScores();
      resetCountdown();
    } catch (e) {
      hapticService.error();
      alertService.showAlert('Error', 'Failed to retry. Please check your connection and try again.');
    }
  };
  
  const handleScorePress = async (value) => {
    if (isConsultingScores) {
      hapticService.error();
      alertService.showAlert('Consult Mode', 'Score changes are disabled in consultation mode.');
      return;
    }

    if (selectedLap === null || selectedSection === null) {
      hapticService.error();
      alertService.showAlert('Warning', 'Please select a section and lap first.');
      return;
    }

    if (!sheetId || !user || !competitionName || !riderName || !getValidAccessToken) {
      hapticService.error();
      alertService.showAlert('Error', 'Missing required information');
      return;
    }

    const lapSection = `LAP${selectedLap}_S${selectedSection}`;
    
    if (wasScoreInitiallyPresent(lapSection)) {
      alertService.showAlert(
        'Modify Existing Score',
        'This section already has a score. Are you sure you want to modify it?',
        [
          { 
            text: 'Cancel', 
            style: 'cancel',
            onPress: () => {
              hapticService.button();
            }
          },
          { 
            text: 'Yes, Modify', 
            onPress: () => {
              performScoreUpdate(value);
            }
          }
        ]
      );
      return;
    }

    performScoreUpdate(value);
  };

  // Effectuer la mise à jour du score
  const performScoreUpdate = async (value) => {
    const lapSection = `LAP${selectedLap}_S${selectedSection}`;
    const previousScore = scores[lapSection];
    
    hapticService.score();
    
    setSyncing(true);
    
    try {
      await sheetService.updateScore(
        sheetId,
        competitionName,
        riderName,
        lapSection,
        value,
        getValidAccessToken
      );
      
      const updatedScores = {
        ...scores,
        [lapSection]: value
      };
      setScores(updatedScores);
      
      setError(null);
      setErrorType(null);
      
      hapticService.success();
      
      setTimeout(() => {
        const isAddingScore = (previousScore === '-' || !previousScore) && value !== '-';
        const isRemovingScore = previousScore && previousScore !== '-' && value === '-';
        
        if (isAddingScore) {
          checkAndAutoSelectNextLap();
        } else if (isRemovingScore) {
          console.log('🔄 Score removed, skipping auto-lap logic');
        } else {
          checkAndAutoSelectNextLap();
        }
      }, 100);
      
    } catch (e) {
      console.error('Error updating score:', e);
      
      if (e instanceof sheetService.ApiError) {
        if (e.type === 'session') {
          return;
        }
      }
      
      hapticService.error();
      alertService.showAlert('Error', 'Failed to update score. Please try again.');
    } finally {
      setSyncing(false);
    }
  };
  
  const getCurrentScore = () => {
    if (selectedLap === null || selectedSection === null) return '-';
    const lapSection = `LAP${selectedLap}_S${selectedSection}`;
    return scores[lapSection] || '-';
  };
  
  const calculateTotalScore = () => {
    let total = 0;
    
    Object.values(scores).forEach(score => {
      if (score !== '-') {
        total += parseInt(score, 10) || 0;
      }
    });
    
    return total;
  };

  // Calculer le statut des tours
  const getLapStatus = (lapNumber) => {
    if (isLapComplete(lapNumber)) {
      return 'complete';
    }
    
    // Sécurité : si availableSections n'est pas encore chargé, retourner 'empty'
    if (!availableSections || availableSections.length === 0) {
      return 'empty';
    }
    
    let hasScores = false;
    // Utiliser availableSections au lieu de 1..sections
    for (const sectionNum of availableSections) {
      const lapSection = `LAP${lapNumber}_S${sectionNum}`;
      const score = scores[lapSection];
      if (score && score !== '-') {
        hasScores = true;
        break;
      }
    }
    
    return hasScores ? 'inprogress' : 'empty';
  };

  // Obtenir le style du bouton de tour
  const getLapButtonStyle = (lapNumber, isConsulting = false) => {
    const isSelected = isConsulting ? consultingSelectedLap === lapNumber : selectedLap === lapNumber;
    const status = getLapStatus(lapNumber);
    
    if (isSelected) {
      if (status === 'complete') {
        return [styles.selectionButton, styles.selectedButton, styles.selectedCompleteButton];
      } else if (status === 'inprogress') {
        return [styles.selectionButton, styles.selectedButton, styles.selectedInProgressButton];
      } else {
        return [styles.selectionButton, styles.selectedButton];
      }
    } else if (status === 'complete') {
      return [styles.selectionButton, styles.completedButton];
    } else if (status === 'inprogress') {
      return [styles.selectionButton, styles.inProgressButton];
    } else {
      return styles.selectionButton;
    }
  };

  const getLapButtonTextStyle = (lapNumber, isConsulting = false) => {
    const isSelected = isConsulting ? consultingSelectedLap === lapNumber : selectedLap === lapNumber;
    const status = getLapStatus(lapNumber);
    
    if (isSelected) {
      return [styles.selectionButtonText, styles.selectedButtonText];
    } else if (status === 'complete') {
      return [styles.selectionButtonText, styles.completedButtonText];
    } else if (status === 'inprogress') {
      return [styles.selectionButtonText, styles.inProgressButtonText];
    } else {
      return styles.selectionButtonText;
    }
  };
  
  const handleSelectRider = () => {
    hapticService.button();
    navigation.goBack();
  };
  
  const handleBackToCourses = () => {
    hapticService.button();
    navigation.navigate('Home');
  };

  // Toggle consultation mode
  const toggleConsultationMode = () => {
    hapticService.button();
    setIsConsultingScores(!isConsultingScores);
    if (!isConsultingScores) {
      setConsultingSelectedLap(1);
    }
  };

  // Fonction pour obtenir le message d'erreur approprié
  const getErrorMessage = () => {
    if (errorType === 'session') {
      return 'Session expired. Please sign in again.';
    }
    return error;
  };

  // Déterminer si on doit afficher le bouton retry
  const shouldShowRetry = error && errorType && errorType !== 'session';

  const canScore = selectedLap !== null && selectedSection !== null;

  // Nouveau composant Modal pour la sélection de section (simplifié)
  const SectionSelectionModal = () => (
    <Modal
      visible={showSectionModal}
      transparent={true}
      animationType="fade"
      onRequestClose={() => {
        if (referenceSectionForSession !== null) {
          setShowSectionModal(false);
        }
      }}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContainer}>
          {/* 🆕 NOUVELLE ICÔNE PIN */}
          <View style={styles.modalIconContainer}>
            <Ionicons name="location" size={48} color="#0066cc" />
          </View>
          {/* Titre sur toute la largeur */}
          <Text style={styles.modalTitle}>
            Change your assigned section
          </Text>
          
          {/* Boutons en grille */}
          <View style={styles.modalButtonsGrid}>
            {availableSections.map(section => (
              <TouchableOpacity
                key={`modal-section-${section}`}
                style={[
                  styles.modalSectionButton,
                  section === referenceSectionForSession && styles.modalCurrentAssignedButton
                ]}
                onPress={() => handleSectionChange(section)}
              >
                <Text style={[
                  styles.modalSectionButtonText,
                  section === referenceSectionForSession && styles.modalCurrentAssignedButtonText
                ]}>
                  S{section}
                </Text>
                {section === referenceSectionForSession && (
                  <View style={styles.modalCurrentAssignedIndicator}>
                    <View style={styles.assignedSectionDot} />
                  </View>
                )}
              </TouchableOpacity>
            ))}
          </View>
          
          {/* Légende centrée simplifiée */}
          {referenceSectionForSession !== null && (
            <View style={styles.modalLegendCentered}>
              <View style={styles.assignedSectionDot} />
              <Text style={styles.modalLegendText}>Your current assigned section</Text>
            </View>
          )}
          
          {/* Bouton Cancel centré */}
          {referenceSectionForSession !== null && (
            <TouchableOpacity
              style={styles.modalCancelButtonCentered}
              onPress={() => {
                hapticService.button();
                setShowSectionModal(false);
              }}
            >
              <Text style={styles.modalCancelButtonText}>Cancel</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    </Modal>
  );
  
  return (
    <SafeAreaView style={styles.container} edges={['bottom']}>
      {/* Modal de sélection de section */}
      <SectionSelectionModal />
      
      {error && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{getErrorMessage()}</Text>
          {errorType === 'session' ? (
            <TouchableOpacity 
              style={styles.retryButton} 
              onPress={async () => {
                hapticService.button();
                await signOut();
                navigation.reset({
                  index: 0,
                  routes: [{ name: 'Login' }]
                });
              }}
            >
              <Text style={styles.retryText}>Reconnect</Text>
            </TouchableOpacity>
          ) : (shouldShowRetry && (
            <TouchableOpacity 
              style={[
                styles.retryButton, 
                isDisabled && styles.retryButtonDisabled
              ]} 
              onPress={handleRetry}
              disabled={isDisabled}
            >
              <Text style={[
                styles.retryText,
                isDisabled && styles.retryTextDisabled
              ]}>
                {getButtonText()}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      )}
      
      {/* Loading uniforme avec le nouveau composant - condition stricte pour éviter le flash */}
      {(isLoading || !assignedSection || selectedSection === null || Object.keys(scores).length === 0) ? (
        <LoadingScreen message="Loading scores..." variant="content" />
      ) : (
        <ScrollView contentContainerStyle={styles.content}>
          {/* Carte principale unique */}
          <View style={styles.mainCard}>
            {/* HEADER - Informations du pilote */}
            <View style={styles.riderHeaderContainer}>
              <View style={styles.numberCircle}>
                <Text style={styles.riderNumber}>{riderNumber}</Text>
              </View>
              <View style={styles.riderDetails}>
                <Text style={styles.riderName}>{riderName}</Text>
                <Text style={styles.riderCountry}>{riderCountry}</Text>
              </View>
            </View>
            
            {/* Score total sur toute la largeur */}
            <TouchableOpacity
              style={styles.totalScoreBar}
              onPress={toggleConsultationMode}
            >
              <View style={styles.totalScoreContent}>
                <View style={styles.totalScoreLeft}>
                  <Ionicons name="trophy-outline" size={24} color="#555" />
                  <Text style={styles.totalScoreLabel}>Total Score</Text>
                </View>
                <View style={styles.totalScoreRight}>
                  <Text style={styles.totalScoreValue}>{calculateTotalScore()}</Text>
                  <Ionicons 
                    name={isConsultingScores ? "eye-off-outline" : "eye-outline"} 
                    size={20} 
                    color="#555" 
                  />
                </View>
              </View>
            </TouchableOpacity>

            {/* MODE CONSULTATION */}
            {isConsultingScores && (
              <>
                {/* Affichage des tours avec résultats */}
                {Array.from({ length: laps }, (_, lapIndex) => {
                  const lapNumber = lapIndex + 1;
                  return (
                    <View key={`consult-lap-${lapNumber}`} style={styles.consultLapSection}>
                      <Text style={styles.consultLapTitle}>Lap {lapNumber}</Text>
                      
                      <View style={styles.consultResultsRow}>
                        {availableSections.map(sectionNumber => {
                          const lapSection = `LAP${lapNumber}_S${sectionNumber}`;
                          const score = scores[lapSection] || '-';
                          const isLastSection = sectionNumber === availableSections[availableSections.length - 1];
                          
                          return (
                            <View
                              key={`consult-result-${lapNumber}-${sectionNumber}`}
                              style={[
                                styles.consultResultItem,
                                isLastSection && styles.consultResultItemLast
                              ]}
                            >
                              <Text style={styles.consultResultLabel}>
                                S{sectionNumber}
                              </Text>
                              <Text style={[
                                styles.consultResultScore,
                                score === '-' && styles.consultResultScoreEmpty
                              ]}>
                                {score}
                              </Text>
                            </View>
                          );
                        })}
                      </View>
                    </View>
                  );
                })}
              </>
            )}

            {/* MODE SCORING NORMAL */}
            {!isConsultingScores && (
              <>
                {/* Message d'instruction si nécessaire */}
                {!canScore && (
                  <View style={styles.instructionContainer}>
                    <Ionicons name="information-circle-outline" size={20} color="#0066cc" />
                    <Text style={styles.instructionText}>
                      {selectedLap === null && selectedSection === null 
                        ? "Select a section and lap to start scoring"
                        : selectedLap === null 
                        ? "Select a lap"
                        : "Select your assigned section"}
                    </Text>
                  </View>
                )}
                
                {/* Section et Laps côte à côte */}
                <View style={styles.sectionLapContainer}>
                  {/* Section - 1/3 de l'espace */}
                  <View style={styles.sectionColumn}>
                    <Text style={styles.selectionTitle}>
                      Section {selectedSection === null && <Text style={styles.required}>*</Text>}
                    </Text>
                    
                    {referenceSectionForSession && (
                      <View style={styles.singleSectionContainerCentered}>
                        <TouchableOpacity
                          style={[styles.selectionButton, styles.selectedButton, styles.singleSectionButton]}
                          onPress={() => {
                            alertService.showAlert(
                              'Change Assigned Section',
                              'Are you sure you want to change your assigned section?',
                              [
                                { 
                                  text: 'Cancel', 
                                  style: 'cancel',
                                  onPress: () => hapticService.button()
                                },
                                { 
                                  text: 'OK', 
                                  onPress: () => {
                                    hapticService.button();
                                    setShowSectionModal(true); // Ouvrir le modal
                                  }
                                }
                              ]
                            );
                          }}
                        >
                          <View style={styles.sectionButtonContent}>
                            <Ionicons name="location" size={16} color="#fff" />
                            <Text style={[styles.selectionButtonText, styles.selectedButtonText, styles.sectionButtonText]}>
                              {referenceSectionForSession}
                            </Text>
                          </View>
                          <View style={styles.assignedSectionIndicator}>
                            <View style={styles.assignedSectionDot} />
                          </View>
                        </TouchableOpacity>
                      </View>
                    )}
                  </View>

                  {/* Séparateur vertical */}
                  <View style={styles.verticalSeparator} />
                  
                  {/* Laps - 2/3 de l'espace */}
                  <View style={styles.lapColumn}>
                    <Text style={styles.selectionTitle}>
                      Lap {selectedLap === null && <Text style={styles.required}>*</Text>}
                      {selectedLap && (
                        <Text style={styles.lapStatusText}>
                          {isLapComplete(selectedLap) ? ' (Complete)' : ' (In Progress)'}
                        </Text>
                      )}
                    </Text>
                    
                    <View style={styles.lapGridCentered}>
                      {Array.from({ length: laps }, (_, i) => i + 1).map(lap => {
                        const status = getLapStatus(lap);
                        return (
                          <TouchableOpacity
                            key={`lap-${lap}`}
                            style={[
                              getLapButtonStyle(lap),
                              styles.lapButton
                            ]}
                            onPress={() => handleLapChange(lap)}
                          >
                            <Text style={getLapButtonTextStyle(lap)}>
                              L{lap}
                            </Text>
                            {status === 'complete' && (
                              <View style={styles.statusIndicator}>
                                <View style={styles.completeIndicator} />
                              </View>
                            )}
                            {status === 'inprogress' && (
                              <View style={styles.statusIndicator}>
                                <View style={styles.inProgressIndicator} />
                              </View>
                            )}
                          </TouchableOpacity>
                        );
                      })}
                    </View>
                  </View>
                </View>
                
                {/* LÉGENDES */}
                <View style={styles.compactLegendsContainer}>
                  <View style={styles.legendRow}>
                    <View style={styles.legendItem}>
                      <View style={styles.completeIndicator} />
                      <Text style={styles.legendText}>Complete</Text>
                    </View>
                    <View style={styles.legendItem}>
                      <View style={styles.inProgressIndicator} />
                      <Text style={styles.legendText}>In Progress</Text>
                    </View>
                    <View style={styles.legendItem}>
                      <View style={styles.selectedIndicator} />
                      <Text style={styles.legendText}>Selected</Text>
                    </View>
                  </View>
                  
                  {referenceSectionForSession && (
                    <View style={styles.legendRow}>
                      <View style={styles.legendItem}>
                        <View style={styles.assignedSectionDot} />
                        <Text style={styles.legendText}>Your assigned section</Text>
                      </View>
                    </View>
                  )}
                </View>
                
                {/* Séparateur */}
                <View style={styles.separator} />
                
                {/* Titre de scoring */}
                <Text style={styles.scoreTitle}>
                  {canScore 
                    ? `Score Section ${selectedSection} - Lap ${selectedLap}`
                    : 'Assign Score'
                  }
                </Text>
                
                {/* Boutons de score */}
                <View style={styles.scoreButtonsContainer}>
                  <View style={styles.scoreButtonsRow}>
                    {pointValues.slice(0, 4).map(value => (
                      <TouchableOpacity
                        key={`score-${value}`}
                        style={[
                          styles.scoreButton,
                          value === '-' && (
                            getCurrentScore() === value.toString() 
                              ? styles.notScoredButtonActive 
                              : styles.notScoredButton
                          ),
                          value !== '-' && getCurrentScore() === value.toString() && styles.activeScoreButton,
                          (syncing || !canScore) && styles.disabledButton
                        ]}
                        onPress={() => handleScorePress(value.toString())}
                        disabled={syncing || !canScore}
                      >
                        <Text
                          style={[
                            styles.scoreButtonText,
                            value === '-' && styles.notScoredButtonText,
                            value !== '-' && getCurrentScore() === value.toString() && styles.activeScoreButtonText,
                            !canScore && styles.disabledButtonText
                          ]}
                        >
                          {value}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                  
                  <View style={styles.scoreButtonsRow}>
                    {pointValues.slice(4).map(value => (
                      <TouchableOpacity
                        key={`score-${value}`}
                        style={[
                          styles.scoreButton,
                          value === '-' && styles.notScoredButton,
                          getCurrentScore() === value.toString() && styles.activeScoreButton,
                          (syncing || !canScore) && styles.disabledButton
                        ]}
                        onPress={() => handleScorePress(value.toString())}
                        disabled={syncing || !canScore}
                      >
                        <Text
                          style={[
                            styles.scoreButtonText,
                            value === '-' && styles.notScoredButtonText,
                            getCurrentScore() === value.toString() && styles.activeScoreButtonText,
                            !canScore && styles.disabledButtonText
                          ]}
                        >
                          {value}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>
                
                {/* Légendes de scoring */}
                <View style={styles.scoringLegendsRow}>
                  {canScore && (
                    <View style={styles.currentScoreLegend}>
                      <View style={styles.selectedIndicator} />
                      <Text style={styles.legendText}>Current score</Text>
                    </View>
                  )}
                  
                  <View style={styles.notScoredLegend}>
                    <View style={styles.notScoredIndicator} />
                    <Text style={styles.legendText}>Not scored</Text>
                  </View>
                </View>
                
                {/* Indicateur de synchronisation */}
                {syncing && (
                  <LoadingScreen message="Syncing..." variant="inline" />
                )}
              </>
            )}
          </View>
          
          {/* Boutons de navigation - cachés en mode consultation */}
          {!isConsultingScores && (
            <>
              {/* Bouton Admin Quick Edit - pleine largeur au-dessus */}
              {isUserAdmin() && (
                <View style={styles.adminQuickEditContainer}>
                  <TouchableOpacity
                    style={styles.adminQuickEditButtonFull}
                    onPress={openAdminQuickEdit}
                  >
                    <Ionicons name="flash-outline" size={20} color="#fff" />
                    <Text style={styles.adminQuickEditButtonText}>Quick Edit</Text>
                  </TouchableOpacity>
                </View>
              )}
              
              {/* Autres boutons navigation */}
              <View style={styles.navigationContainer}>
                <TouchableOpacity
                  style={styles.navigationButton}
                  onPress={handleSelectRider}
                >
                  <Ionicons name="people-outline" size={20} color="#fff" />
                  <Text style={styles.navigationButtonText}>Select Pilot</Text>
                </TouchableOpacity>
                
                <TouchableOpacity
                  style={styles.navigationButton}
                  onPress={handleBackToCourses}
                >
                  <Ionicons name="list-outline" size={20} color="#fff" />
                  <Text style={styles.navigationButtonText}>Select Races</Text>
                </TouchableOpacity>
              </View>
            </>
          )}
        </ScrollView>
      )}
      {/* Modal Admin Quick Edit */}
        <AdminQuickEditModal
          visible={showAdminQuickEdit}
          onClose={closeAdminQuickEdit}
          competitionName={competitionName}
          riderName={riderName}
          riderNumber={riderNumber}
          laps={laps}
          sections={sections}
          availableSections={availableSections} // 🔧 AJOUTER
          initialScores={scores}
        />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  singleSectionButtonWithPin: {
    width: 80,  // Plus large que 48px pour accommoder l'icône + texte
    height: 48,
    marginRight: 0,
    marginBottom: 6,
    paddingHorizontal: 8, // Padding interne
  },
  sectionButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  sectionButtonText: {
    fontSize: 22, // Légèrement plus petit pour s'adapter
    marginLeft: 3, // Espace entre l'icône et le texte
  },
  modalIconContainer: {
    alignItems: 'center',
    marginBottom: 16,
  },
  adminQuickEditContainer: {
  paddingHorizontal: 0,
  paddingBottom: 8,
  },
  adminQuickEditButtonFull: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#e91e63',
    paddingVertical: 12,
    borderRadius: 8,
    width: '100%',
  },
  errorContainer: {
    margin: 16,
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#ffebee',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  errorText: {
    color: '#d32f2f',
    fontFamily: 'Montserrat_400Regular',
    flex: 1,
    marginRight: 12,
  },
  retryButton: {
    backgroundColor: '#0066cc',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 4,
  },
  retryButtonDisabled: {
    backgroundColor: '#cccccc',
  },
  retryText: {
    color: '#fff',
    
    fontFamily: 'Montserrat_700Bold',
    fontSize: 12,
  },
  retryTextDisabled: {
    color: '#999999',
  },
  content: {
    padding: 16,
  },
  mainCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  
  // NOUVEAU MODAL STYLES
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 24,
    maxWidth: 500,
    width: '100%',
    maxHeight: '80%',
  },
  modalTitle: {
    fontSize: 22,
    
    color: '#333',
    textAlign: 'center',
    marginBottom: 32,
    fontFamily: 'Montserrat_700Bold',
  },
  modalButtonsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 24,
    gap: 16,
  },
  modalSectionButton: {
    backgroundColor: '#f0f0f0',
    borderRadius: 12,
    width: 64,
    height: 64,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#e0e0e0',
    position: 'relative',
  },
  modalCurrentAssignedButton: {
    backgroundColor: 'rgba(244, 67, 54, 0.1)',
    borderColor: '#f44336',
  },
  modalSectionButtonText: {
    color: '#555',
    
    fontSize: 24,
    fontFamily: 'Montserrat_700Bold',
  },
  modalCurrentAssignedButtonText: {
    color: '#555',
  },
  modalCurrentAssignedIndicator: {
    position: 'absolute',
    top: 6,
    right: 6,
  },
  modalLegendCentered: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
  },
  modalLegendText: {
    fontSize: 14,
    color: '#555',
    marginLeft: 8,
    fontFamily: 'Montserrat_400Regular',
  },
  modalCancelButtonCentered: {
    backgroundColor: '#6c757d',
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 24,
    alignSelf: 'center',
  },
  modalCancelButtonText: {
    color: '#fff',
    fontSize: 16,
    
    fontFamily: 'Montserrat_600SemiBold',
  },
  
  // HEADER REDESIGN
  riderHeaderContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  numberCircle: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: '#0066cc',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  riderNumber: {
    color: '#fff',
    
    fontSize: 20,
    fontFamily: 'Montserrat_700Bold',
  },
  riderDetails: {
    flex: 1,
  },
  riderName: {
    fontSize: 22,
    
    color: '#333',
    marginBottom: 4,
    fontFamily: 'Montserrat_700Bold',
  },
  riderCountry: {
    fontSize: 16,
    color: '#666',
    fontFamily: 'Montserrat_400Regular',
  },
  
  // SCORE TOTAL BAR
  totalScoreBar: {
    backgroundColor: '#f8f9fa',
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#e9ecef',
  },
  totalScoreContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  totalScoreLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  totalScoreLabel: {
    fontSize: 18,
    
    color: '#555',
    marginLeft: 8,
    fontFamily: 'Montserrat_600SemiBold',
  },
  totalScoreRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  totalScoreValue: {
    fontSize: 28,
    
    color: '#333',
    marginRight: 12,
    fontFamily: 'Montserrat_700Bold',
  },
  
  separator: {
    height: 1,
    backgroundColor: '#e0e0e0',
    marginBottom: 16,
  },

  // STYLES POUR NOUVEAU MODE CONSULTATION
  consultLapSection: {
    marginBottom: 16,
  },
  consultLapTitle: {
    fontSize: 20,
    
    color: '#333',
    fontFamily: 'Montserrat_700Bold',
    marginBottom: 12,
    textAlign: 'center',
  },
  consultResultsRow: {
    backgroundColor: '#0066cc',
    borderRadius: 8,
    flexDirection: 'row',
    marginBottom: 8,
    minHeight: 60,
    alignItems: 'center',
  },
  consultResultItem: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRightWidth: 1,
    borderRightColor: '#ffffff',
  },
  consultResultItemLast: {
    borderRightWidth: 0,
  },
  consultResultLabel: {
    fontSize: 16,
    
    color: '#ffffff',
    fontFamily: 'Montserrat_400Regular',
    marginBottom: 4,
  },
  consultResultScore: {
    fontSize: 30,
    
    color: '#ffffff',
    fontFamily: 'Montserrat_700Bold',
  },
  consultResultScoreEmpty: {
    color: '#cccccc',
  },

  // STYLES EXISTANTS
  instructionContainer: {
    backgroundColor: '#e3f2fd',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
    flexDirection: 'row',
    alignItems: 'center',
    borderLeftWidth: 3,
    borderLeftColor: '#0066cc',
  },
  instructionText: {
    fontSize: 14,
    color: '#0066cc',
    marginLeft: 8,
    flex: 1,
    fontFamily: 'Montserrat_500Medium',
  },
  sectionLapContainer: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  sectionColumn: {
    flex: 1,
    paddingRight: 8,
  },
  verticalSeparator: {
    width: 1,
    backgroundColor: '#e0e0e0',
    marginHorizontal: 8,
  },
  lapColumn: {
    flex: 2,
    paddingLeft: 8,
  },
  singleSectionContainerCentered: {
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 54,
  },
  lapGridCentered: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
  },
  lapButton: {
    width: 48,
    height: 48,
    marginRight: 8,
    marginBottom: 6,
  },
  singleSectionButton: {
    width: 48,
    height: 48,
    marginRight: 0,
    marginBottom: 8,
  },
  
  // LÉGENDES COMPACTES
  compactLegendsContainer: {
    marginBottom: 12,
    paddingHorizontal: 8,
  },
  legendRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginVertical: 2,
    flexWrap: 'wrap',
  },
  
  selectionTitle: {
    fontSize: 18,
    
    color: '#333',
    marginBottom: 12,
    textAlign: 'center',
    fontFamily: 'Montserrat_700Bold',
  },
  lapStatusText: {
    fontSize: 14,
    
    color: '#666',
    fontFamily: 'Montserrat_400Regular',
  },
  required: {
    color: '#f44336',
  },
  selectionButton: {
    backgroundColor: '#f0f0f0',
    borderRadius: 8,
    width: 48,
    height: 48,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 8,
    marginBottom: 6,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  selectedButton: {
    backgroundColor: '#0066cc',
    borderColor: '#0066cc',
    borderWidth: 2,
  },
  selectedCompleteButton: {
    borderColor: '#4caf50',
    borderWidth: 2,
  },
  selectedInProgressButton: {
    borderColor: '#ff9800',
    borderWidth: 2,
  },
  completedButton: {
    backgroundColor: '#f0f8f0',
    borderWidth: 2,
    borderColor: '#4caf50',
  },
  inProgressButton: {
    backgroundColor: '#fff3e0',
    borderWidth: 2,
    borderColor: '#ff9800',
  },
  selectionButtonText: {
    color: '#555',
    
    fontSize: 22,
    fontFamily: 'Montserrat_700Bold',
  },
  selectedButtonText: {
    color: '#fff',
  },
  completedButtonText: {
    color: '#2e7d32',
  },
  inProgressButtonText: {
    color: '#f57c00',
  },
  scoreTitle: {
    fontSize: 18,
    
    color: '#333',
    marginBottom: 16,
    textAlign: 'center',
    fontFamily: 'Montserrat_700Bold',
  },
  scoreButtonsContainer: {
    alignItems: 'center',
  },
  scoreButtonsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    maxWidth: 320,
    marginBottom: 16,
  },
  scoreButton: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: '#f0f0f0',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 0,
    borderColor: '#0066cc',
  },
  notScoredButton: {
    backgroundColor: '#f0f0f0',
    borderColor: '#999999',
    borderWidth: 3,
  },
  notScoredButtonActive: {
    backgroundColor: '#999999',
    borderColor: '#999999',
    borderWidth: 2,
  },
  activeScoreButton: {
    backgroundColor: '#0066cc',
  },
  disabledButton: {
    opacity: 0.3,
  },
  scoreButtonText: {
    fontSize: 25,
    
    color: '#555',
    fontFamily: 'Montserrat_700Bold',
  },
  notScoredButtonText: {
    color: '#999999',
  },
  activeScoreButtonText: {
    color: '#fff',
  },
  disabledButtonText: {
    color: '#ccc',
  },
  statusIndicator: {
    position: 'absolute',
    top: 2,
    right: 2,
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  assignedSectionIndicator: {
    position: 'absolute',
    top: 2,
    right: 2,
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  completeIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#4caf50',
  },
  inProgressIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#ff9800',
  },
  selectedIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#0066cc',
  },
  assignedSectionDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#f44336',
  },
  notScoredIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#999999',
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 6,
    marginVertical: 2,
  },
  legendText: {
    fontSize: 12,
    color: '#555',
    fontFamily: 'Montserrat_400Regular',
    marginLeft: 4,
  },
  
  // LÉGENDES DE SCORING
  scoringLegendsRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 16,
  },
  notScoredLegend: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight:8,
    marginLeft: 8,
  },
  currentScoreLegend: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 8,
  },
  
  navigationContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
  },
  navigationButton: {
    backgroundColor: '#0066cc',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 0.48,
  },
  navigationButtonText: {
    color: '#fff',
    
    fontSize: 16,
    marginLeft: 8,
    fontFamily: 'Montserrat_600SemiBold',
  },
  adminQuickEditButton: {
    backgroundColor: '#e91e63',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 0.32,
  marginRight: 8,
  },
  adminQuickEditButtonText: {
    color: '#fff',
    
    fontSize: 14,
    marginLeft: 8,
    fontFamily: 'Montserrat_600SemiBold',
  },
});

export default ScoringScreen;